/* eslint-disable no-param-reassign */
const CRC32 = require('crc-32');
const store = require('./store');

const purchaseEventStorageKey = '_constructorio_purchase_order_ids';

const PII_REGEX = [
  {
    pattern: /[\w\-+\\.]+@([\w-]+\.)+[\w-]{2,4}/,
    replaceWith: '<email_omitted>',
  },
  {
    pattern: /^(?:\+\d{11,12}|\+\d{1,3}\s\d{3}\s\d{3}\s\d{3,4}|\(\d{3}\)\d{7}|\(\d{3}\)\s\d{3}\s\d{4}|\(\d{3}\)\d{3}-\d{4}|\(\d{3}\)\s\d{3}-\d{4})$/,
    replaceWith: '<phone_omitted>',
  },
  {
    pattern: /^(?:4[0-9]{15}|(?:5[1-5][0-9]{2}|222[1-9]|22[3-9][0-9]|2[3-6][0-9]{2}|27[01][0-9]|2720)[0-9]{12}|3[47][0-9]{13}|3(?:0[0-5]|[68][0-9])[0-9]{11}|6(?:011|5[0-9]{2})[0-9]{12}|(?:2131|1800|35\d{3})\d{11})$/, // Visa, Mastercard, Amex, Discover, JCB and Diners Club, regex source: https://www.regular-expressions.info/creditcard.html
    replaceWith: '<credit_omitted>',
  },
  // Add more PII REGEX
];
const URL_MAX_LEN = 2000;

const utils = {
  trimNonBreakingSpaces: (string) => string.replace(/\s/g, ' ').trim(),

  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/encodeURIComponent#encoding_for_rfc3986
  encodeURIComponentRFC3986: (string) => encodeURIComponent(string).replace(/[!'()*]/g, (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`),

  cleanParams: (paramsObj) => {
    const cleanedParams = {};

    Object.keys(paramsObj).forEach((paramKey) => {
      const paramValue = paramsObj[paramKey];

      if (typeof paramValue === 'string') {
        // Replace non-breaking spaces (or any other type of spaces caught by the regex)
        // - with a regular white space
        cleanedParams[paramKey] = utils.trimNonBreakingSpaces(paramValue);
      } else {
        cleanedParams[paramKey] = paramValue;
      }
    });

    return cleanedParams;
  },
  trimUrl: (urlObj, maxLen = URL_MAX_LEN) => {
    let urlString = urlObj.toString();

    if (urlString.length <= maxLen) {
      return urlString;
    }

    const urlCopy = new URL(urlObj.toString());
    const { searchParams } = urlCopy;
    const paramEntries = Array.from(searchParams.entries());

    if (paramEntries.length === 0) {
      return utils.truncateString(urlString, maxLen);
    }

    paramEntries.sort((a, b) => {
      const aLength = a[0].length + a[1].length;
      const bLength = b[0].length + b[1].length;
      return bLength - aLength;
    });

    for (let i = 0; i < paramEntries.length; i += 1) {
      if (urlString.length <= maxLen) {
        break;
      }
      searchParams.delete(paramEntries[i][0]);
      urlString = urlCopy.toString();
    }

    if (urlString.length > maxLen) {
      return utils.truncateString(urlString, maxLen);
    }

    return urlString;
  },

  cleanAndValidateUrl: (url, baseUrl = undefined) => {
    let validatedUrl = null;

    try {
      // Handle android app referrers
      if (url?.startsWith('android-app')) {
        url = url?.replace('android-app', 'https');
      }

      const urlObj = new URL(url, baseUrl);
      const trimmedUrl = new URL(utils.trimUrl(urlObj));

      validatedUrl = trimmedUrl.toString();
    } catch (e) {
      // do nothing
    }

    return validatedUrl;
  },

  throwHttpErrorFromResponse: (error, response) => response.json().then((json) => {
    error.message = json.message;
    error.status = response.status;
    error.statusText = response.statusText;
    error.url = response.url;
    error.headers = response.headers;

    throw error;
  }),

  canUseDOM: () => !!(
    (typeof window !== 'undefined' && window.document && window.document.createElement)
  ),

  addEventListener: (eventType, callback, useCapture) => {
    if (utils.canUseDOM()) {
      window.addEventListener(eventType, callback, useCapture);
    }
  },

  removeEventListener: (eventType, callback, useCapture) => {
    if (utils.canUseDOM()) {
      window.removeEventListener(eventType, callback, useCapture);
    }
  },

  getNavigator: () => {
    if (utils.canUseDOM()) {
      return window.navigator;
    }

    return {
      userAgent: '',
      webdriver: false,
    };
  },

  isNil: (value) => value == null,

  getWindowLocation: () => {
    if (utils.canUseDOM()) {
      return window.location;
    }

    return {};
  },

  getDocumentReferrer: () => {
    let documentReferrer = null;

    try {
      if (utils.canUseDOM()) {
        documentReferrer = utils.cleanAndValidateUrl(document.referrer);
      }
    } catch (e) {
      // do nothing
    }

    return documentReferrer;
  },

  getCanonicalUrl: () => {
    let canonicalURL = null;

    try {
      if (utils.canUseDOM()) {
        const linkEle = document?.querySelector('link[rel="canonical"]');
        const href = linkEle?.getAttribute('href');

        if (href) {
          canonicalURL = utils.cleanAndValidateUrl(href, document.location.href);
        }
      }
    } catch (e) {
      // do nothing
    }

    return canonicalURL;
  },

  dispatchEvent: (event) => {
    if (utils.canUseDOM()) {
      window.dispatchEvent(event);
    }
  },

  createCustomEvent: (eventName, detail) => {
    if (utils.canUseDOM()) {
      try {
        return new window.CustomEvent(eventName, { detail });
      } catch (e) {
        const evt = document.createEvent('CustomEvent');

        evt.initCustomEvent(eventName, false, false, detail);

        return evt;
      }
    }

    return null;
  },

  hasOrderIdRecord({ orderId, apiKey }) {
    let orderPerKeyId = orderId;
    if (apiKey) orderPerKeyId = `${apiKey}-${orderId}`;

    const orderIdHash = CRC32.str(orderPerKeyId.toString());
    let purchaseEventStorage = store.local.get(purchaseEventStorageKey);

    if (typeof purchaseEventStorage === 'string') {
      purchaseEventStorage = JSON.parse(purchaseEventStorage);
    }
    if (purchaseEventStorage && purchaseEventStorage.includes(orderIdHash)) {
      return true;
    }

    return null;
  },

  addOrderIdRecord({ orderId, apiKey }) {
    let orderPerKeyId = orderId;
    if (apiKey) orderPerKeyId = `${apiKey}-${orderId}`;

    const orderIdHash = CRC32.str(orderPerKeyId.toString());
    let purchaseEventStorage = store.local.get(purchaseEventStorageKey);

    if (typeof purchaseEventStorage === 'string') {
      purchaseEventStorage = JSON.parse(purchaseEventStorage);
    }

    if (purchaseEventStorage) {
      // If the order already exists, do nothing
      if (purchaseEventStorage.includes(orderIdHash)) {
        return;
      }

      if (purchaseEventStorage.length >= 10) {
        purchaseEventStorage = purchaseEventStorage.slice(-9);
      }
      purchaseEventStorage.push(orderIdHash);
    } else {
      // Create a new object map for the order ids
      purchaseEventStorage = [orderIdHash];
    }

    // Push the order id map into local storage
    store.local.set(purchaseEventStorageKey, purchaseEventStorage);
  },

  // Abort network request based on supplied timeout interval (in milliseconds)
  // - method call parameter takes precedence over global options parameter
  applyNetworkTimeout: (options = {}, networkParameters = {}, controller = null) => {
    const optionsTimeout = options && options.networkParameters && options.networkParameters.timeout;
    const networkParametersTimeout = networkParameters && networkParameters.timeout;
    const timeout = networkParametersTimeout || optionsTimeout;

    if (typeof timeout === 'number' && controller) {
      setTimeout(() => controller.abort(), timeout);
    }
  },
  stringify: (object, prefix, objectType) => {
    if (!object) {
      return '';
    }

    const allValues = [];

    Object.keys(object).forEach((key) => {
      const value = object[key];
      const encodedKey = utils.encodeURIComponentRFC3986(key);

      let stringifiedValue;

      // Check for both null and undefined
      if (value != null) {
        const nextPrefix = prefix ? `${prefix}%5B${encodedKey}%5D` : encodedKey;

        if (Array.isArray(value)) {
          stringifiedValue = utils.stringify(value, nextPrefix, 'array');
        } else if (typeof value === 'object') {
          stringifiedValue = utils.stringify(value, nextPrefix, 'object');
        } else if (objectType === 'object') {
          stringifiedValue = `${nextPrefix}=${utils.encodeURIComponentRFC3986(value)}`;
        } else {
          stringifiedValue = `${prefix || encodedKey}=${utils.encodeURIComponentRFC3986(value)}`;
        }

        allValues.push(stringifiedValue);
      }
    });

    return allValues.join('&');
  },

  toSnakeCase: (camelCasedStr) => camelCasedStr.replace(/[A-Z]/g, (prefix) => `_${prefix.toLowerCase()}`),

  toSnakeCaseKeys: (camelCasedObj, toRecurse = false) => {
    const snakeCasedObj = {};
    Object.keys(camelCasedObj).forEach((key) => {
      const newKey = utils.toSnakeCase(key);
      snakeCasedObj[newKey] = toRecurse && typeof camelCasedObj[key] === 'object' && !Array.isArray(camelCasedObj[key])
        ? utils.toSnakeCaseKeys(camelCasedObj[key], toRecurse)
        : camelCasedObj[key];
    });
    return snakeCasedObj;
  },
  containsPii(query, piiPattern) {
    const normalizedTerm = query.toLowerCase();
    return piiPattern.test(normalizedTerm);
  },
  obfuscatePiiRequest(urlString) {
    let obfuscatedUrl = urlString;

    try {
      const url = new URL(urlString);
      const paths = url?.pathname?.split('/');
      const paramValues = url?.search?.split('&')?.map((param) => param.split('=')?.[1]);

      PII_REGEX.forEach(({ pattern, replaceWith }) => {
        paths.forEach((path) => {
          const decodedPath = decodeURIComponent(path);
          if (utils.containsPii(decodedPath, pattern)) {
            obfuscatedUrl = obfuscatedUrl.replaceAll(path, replaceWith);
          }
        });

        paramValues.forEach((paramValue) => {
          const decodedParamValue = decodeURIComponent(paramValue);
          if (utils.containsPii(decodedParamValue, pattern)) {
            obfuscatedUrl = obfuscatedUrl.replaceAll(decodedParamValue, replaceWith);
          }
        });
      });
    } catch (e) {
      // do nothing
    }

    return obfuscatedUrl;
  },
  convertResponseToJson(response) {
    if (response.ok) {
      return response.json()
        .catch(() => response.text()
          .then((responseText) => {
            throw new Error(`Server responded with an invalid JSON object. Response code: ${response.code}, Response: ${responseText}`);
          }));
    }

    return utils.throwHttpErrorFromResponse(new Error(), response);
  },
  addHTTPSToString(url, allowHttp = false) {
    if (typeof url !== 'string') {
      return null;
    }

    const doesUrlIncludeHTTPS = url.startsWith('https://');
    const doesUrlStartWithHTTP = url.startsWith('http://');

    if (!doesUrlIncludeHTTPS && doesUrlStartWithHTTP) {
      if (allowHttp) {
        return url;
      }
      return url.replace('http', 'https');
    }

    if (!doesUrlStartWithHTTP && !doesUrlIncludeHTTPS) {
      const urlWithHttps = `https://${url}`;

      return urlWithHttps;
    }

    return url;
  },

  truncateString: (string, maxLength) => string.slice(0, maxLength),

  toValidAdditionalTrackingKeys: (additionalTrackingKeys, primaryKey) => {
    if (!Array.isArray(additionalTrackingKeys)) {
      return null;
    }

    const uniqueKeys = [...new Set(
      additionalTrackingKeys.filter((key) => key && typeof key === 'string' && key !== primaryKey),
    )];

    return uniqueKeys.length ? uniqueKeys : null;
  },

  toValidTestCells: (testCells) => {
    if (!testCells || typeof testCells !== 'object' || Array.isArray(testCells)) {
      return {};
    }

    const filtered = {};

    Object.keys(testCells).forEach((key) => {
      if (typeof testCells[key] === 'string' && testCells[key].trim() !== '') {
        filtered[key] = testCells[key];
      }
    });

    return filtered;
  },

  applyWindowParameterGetters: (options) => {
    if (!utils.canUseDOM()) {
      return;
    }

    const defineWindowGetter = (key, windowKey, validate) => {
      const initial = options[key] || undefined;
      let backing = initial;

      Object.defineProperty(options, key, {
        get() {
          if (validate(backing)) return backing;
          const windowValue = (window.cnstrc && window.cnstrc[windowKey]) || window[`cnstrc${windowKey.charAt(0).toUpperCase()}${windowKey.slice(1)}`];
          const validated = validate(windowValue) ? windowValue : undefined;
          return validated;
        },
        set(value) { backing = value; },
        enumerable: true,
        configurable: true,
      });
    };

    defineWindowGetter('userId', 'userId', (v) => typeof v === 'string' && v.length > 0);

    defineWindowGetter('segments', 'userSegments', (v) => Array.isArray(v) && v.length > 0);

    // testCells needs special handling for validation through toValidTestCells
    const initialTestCells = options.testCells || undefined;
    let backingTestCells = initialTestCells;

    Object.defineProperty(options, 'testCells', {
      get() {
        if (backingTestCells && Object.keys(backingTestCells).length > 0) {
          return backingTestCells;
        }
        const windowTestCells = (window.cnstrc && window.cnstrc.testCells) || window.cnstrcTestCells;
        const validated = utils.toValidTestCells(windowTestCells);
        if (validated && Object.keys(validated).length > 0) {
          return validated;
        }
        return undefined;
      },
      set(value) { backingTestCells = value; },
      enumerable: true,
      configurable: true,
    });
  },

  getBehaviorUrl: (mediaServiceUrl) => {
    const baseUrl = new URL(mediaServiceUrl);

    if (!baseUrl.hostname.startsWith('behavior')) {
      baseUrl.hostname = `behavior.${baseUrl.hostname}`;
    }

    return baseUrl;
  },
};

module.exports = utils;
